import { HttpError, SessionClosedError, SessionConflictError } from '@/api/errors'

import type { AdyenClaimSessionResponse, AdyenSessionResponse, AdyenSessionResultResponse } from '@/lib/types'

const TOKEN_PREFIX = 'JWT'

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `${TOKEN_PREFIX} ${token}`,
  }
}

async function post<T>(url: string, body: object, headers: Record<string, string>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new HttpError(`HTTP error - status: ${response.status}`, response.status)
  }
  return (await response.json()) as T
}

/**
 * Internal-api client for Adyen session lifecycle. Ported bit-exact from
 * src/app/adyen/api/sessions-api-client.ts, preserving the known quirks
 * called out in specs/app/index.md::Decisions::2026-05-13:
 *
 *   - createAdyenTokenizationSession sends **only** Content-Type, omitting
 *     the Authorization header (backend convention for tokenization sessions).
 *   - claimSession maps 409 → SessionConflictError, 410 → SessionClosedError;
 *     both errors lack `state` so they fall through to the form-invalid
 *     branch in CardDropIn's handleSubmit (branch 10d).
 */
export class AdyenSessionsClient {
  constructor(
    private readonly host: string,
    private readonly token: string
  ) {}

  async createAdyenSession(encodedSessionData: string): Promise<AdyenSessionResponse> {
    return post<AdyenSessionResponse>(
      `${this.host}/adyen/adyen-session/`,
      { session_data: encodedSessionData },
      authHeaders(this.token)
    )
  }

  async createAdyenTokenizationSession(encodedSessionData: string): Promise<AdyenSessionResponse> {
    // NOTE: deliberately omits the Authorization header — preserved bit-exact
    // from the webpack-era client; this is a backend convention, not a bug.
    return post<AdyenSessionResponse>(
      `${this.host}/adyen/tokenization-sessions/`,
      { session_data: encodedSessionData },
      { 'Content-Type': 'application/json' }
    )
  }

  async claimSession(sessionId: string): Promise<AdyenClaimSessionResponse> {
    try {
      return await post<AdyenClaimSessionResponse>(
        `${this.host}/adyen/adyen-session-claim/`,
        { session_id: sessionId },
        authHeaders(this.token)
      )
    } catch (error) {
      if (error instanceof HttpError && error.status === 409) {
        throw new SessionConflictError('Someone else is currently paying the transaction.')
      }
      if (error instanceof HttpError && error.status === 410) {
        throw new SessionClosedError('The payment has already been paid.')
      }
      throw error
    }
  }

  async sendAdyenSessionResult(args: {
    brand: string
    endDigits: string
    contactId: string
    transactionId: string
    resultCode: string
    sessionId: string
  }): Promise<AdyenSessionResultResponse> {
    return post<AdyenSessionResultResponse>(
      `${this.host}/adyen/adyen-session-result/`,
      {
        brand: args.brand,
        end_digits: args.endDigits,
        contact_id: args.contactId,
        transaction_id: args.transactionId,
        result_code: args.resultCode,
        session_id: args.sessionId,
      },
      authHeaders(this.token)
    )
  }
}
